import { IDeviceCapabilities, IBenchmarkResult } from '@src/types';

/**
 * Check if WebGL2 is supported on this device
 */
export const supportsWebGL2 = (): boolean => {
  try {
    const canvas = new OffscreenCanvas(1, 1);
    const gl = canvas.getContext('webgl2');
    return !!gl;
  } catch {
    return false;
  }
};

/**
 * Detect if the current device is mobile
 */
export const detectIsMobile = (): boolean => {
  return (
    /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 0 && /MacIntel/.test(navigator.platform))
  );
};

/**
 * Detect if the device has a hardware GPU (not software renderer)
 * @returns { hasGPU: boolean, gpuRenderer: string }
 */
export const detectGPU = (): { hasGPU: boolean; gpuRenderer: string } => {
  let hasGPU = false;
  let gpuRenderer = 'Unknown';
  let canvas: HTMLCanvasElement | null = null;

  try {
    canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');

    if (gl) {
      hasGPU = true;
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        gpuRenderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string;
        // Check for software renderers
        if (/SwiftShader|Software|llvmpipe/i.test(gpuRenderer)) {
          hasGPU = false;
        }
      }
      // Cleanup WebGL context
      const loseContext = gl.getExtension('WEBGL_lose_context');
      if (loseContext) {
        loseContext.loseContext();
      }
    }
  } catch (_e) {
    hasGPU = false;
  } finally {
    // Cleanup canvas element
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
      canvas = null;
    }
  }

  return { hasGPU, gpuRenderer };
};

/**
 * Run a universal performance benchmark
 * Works on: Desktop, Mobile, Mac, Windows, iOS, Android
 *
 * Measures actual device capability for VB-relevant operations:
 * - Canvas 2D blur operations (simulates mask edge blur)
 * - Image processing throughput
 *
 * MEMORY SAFETY: Canvas and context are properly cleaned up after benchmark
 *
 * @returns Performance score 0-100 and recommended settings
 */
export const runPerformanceBenchmark = async (): Promise<IBenchmarkResult> => {
  // Use small canvas to minimize memory footprint
  const canvas = new OffscreenCanvas(320, 240);
  const ctx = canvas.getContext('2d') as OffscreenCanvasRenderingContext2D | null;

  if (!ctx) {
    // Fallback for environments without Canvas 2D support
    console.warn('[VB Benchmark] Canvas 2D not supported, using medium defaults');
    return { score: 50, tier: 'medium', recommendedFPS: 15, recommendedScale: 0.55 };
  }

  try {
    // Create test pattern (simulates video frame)
    const imageData = ctx.createImageData(320, 240);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      data[i] = (i * 7) % 256; // R
      data[i + 1] = (i * 13) % 256; // G
      data[i + 2] = (i * 17) % 256; // B
      data[i + 3] = 255; // A
    }
    ctx.putImageData(imageData, 0, 0);

    // Benchmark: Blur + draw operations (simulates VB mask processing)
    const iterations = 15;
    const start = performance.now();

    for (let i = 0; i < iterations; i++) {
      ctx.filter = 'blur(4px)';
      ctx.drawImage(canvas, 0, 0);
      ctx.filter = 'none';
    }

    const elapsed = performance.now() - start;
    const avgFrameTime = elapsed / iterations;

    // Calculate score (0-100) based on frame time
    // Target: < 5ms = 100, > 50ms = 0
    const score = Math.max(0, Math.min(100, Math.round((50 - avgFrameTime) * 2.5)));

    // Determine tier and recommended settings based on actual performance
    let tier: 'low' | 'medium' | 'high';
    let recommendedFPS: number;
    let recommendedScale: number;

    if (score >= 70) {
      // High performance: < ~12ms per frame
      tier = 'high';
      recommendedFPS = 24;
      recommendedScale = 0.85;
    } else if (score >= 40) {
      // Medium performance: ~12-28ms per frame
      tier = 'medium';
      recommendedFPS = 15;
      recommendedScale = 0.55;
    } else {
      // Low performance: > ~28ms per frame
      tier = 'low';
      recommendedFPS = 10;
      recommendedScale = 0.4;
    }

    console.log('[VB Benchmark] Result:', {
      score,
      avgFrameTime: avgFrameTime.toFixed(2) + 'ms',
      tier,
      recommendedFPS,
      recommendedScale,
    });

    return { score, tier, recommendedFPS, recommendedScale };
  } catch (error) {
    console.error('[VB Benchmark] Error:', error);
    // Safe fallback on error
    return { score: 50, tier: 'medium', recommendedFPS: 15, recommendedScale: 0.55 };
  }
  // Note: OffscreenCanvas doesn't need explicit cleanup - it's garbage collected
  // when the function scope ends and there are no more references
};

/**
 * Detect device capabilities using runtime performance benchmark
 * This provides accurate performance tier detection across all device types.
 *
 * @returns Device capabilities with benchmark-derived performance tier
 */
export const detectDeviceCapabilities = async (): Promise<IDeviceCapabilities> => {
  const isMobile = detectIsMobile();
  const cpuCores = navigator.hardwareConcurrency || 4;
  // @ts-expect-error deviceMemory is not in all browsers
  const memoryGB = navigator.deviceMemory || 4;

  const { hasGPU, gpuRenderer } = detectGPU();
  console.log('[VB] Device info:', { isMobile, hasGPU, gpuRenderer, cpuCores, memoryGB });

  // Run actual performance benchmark to determine tier
  const benchmark = await runPerformanceBenchmark();

  // Apply mobile penalty to benchmark values (mobile devices need slightly lower settings)
  const recommendedFPS = isMobile ? Math.max(8, benchmark.recommendedFPS - 4) : benchmark.recommendedFPS;
  const recommendedScale = isMobile ? Math.max(0.3, benchmark.recommendedScale - 0.1) : benchmark.recommendedScale;

  return {
    isMobile,
    hasGPU,
    cpuCores,
    memoryGB,
    performanceTier: benchmark.tier,
    benchmarkScore: benchmark.score,
    recommendedFPS,
    recommendedScale,
  };
};
